import React, { useContext, useState, useEffect } from "react";
import { useMemo } from "react";
import { Flex } from "reflexbox";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import * as yup from "yup";
import { Formik, Form } from "formik";
import FormField from "./FormField";
import styled from "styled-components";
import Loading from "../UI/Loading";

const AnimatedField = styled(Flex)`
  flex-direction: column;
  width: 100%;
  opacity: 1;
  position: relative;
  @keyframes slide-up {
    0% {
      opacity: 0;
      top: 10rem;
    }

    100% {
      opacity: 1;
      top: 0;
    }
  }

  opacity: 1;
  position: relative;

  ${({
    apiCurrentPage,
    optionIndex,
    numItems,
    optionToFocus,
    id,
    hasValue,
    value,
  }) => {
    if (!apiCurrentPage) return "";
    if (optionIndex + 1 < numItems && optionToFocus !== id) return "";
    if (hasValue && value !== "default" && value !== "" && optionToFocus !== id)
      return ".animated-radio {animation-name: unset !important;}";
    return `animation-name: slide-up; animation-duration: 200ms;`;
  }}
  ${({ hasValue, value }) => {
    if (hasValue && value !== "default" && value !== "")
      return `
        .animated-radio {
          animation-name: unset !important;
        }
      `;
  }}
`;

const FormComponent = ({ isLoading }) => {
  const { api, currentPage, optionToFocus } = useContext(ConfiguratorContext);

  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setSchema] = useState(yup.object().shape({}));

  const apiCurrentPage = useMemo(() => {
    return api?.d?.Pages?.[currentPage];
  }, [api, currentPage]);

  const screens = useMemo(() => {
    if (!apiCurrentPage) return [];
    const options = [];
    const { Screens = [] } = apiCurrentPage || {};

    Screens.forEach((Screen) => {
      const { ScreenOptions = [], InformationLink, IsVisible } = Screen || {};
      if (!!IsVisible)
        ScreenOptions.forEach((ScreenOption, optionIndex) => {
          const { ID, IsVisible, Value } = ScreenOption;
          if (!!IsVisible)
            options.push(
              <AnimatedField
                optionToFocus={optionToFocus}
                numItems={ScreenOptions.length}
                optionIndex={optionIndex}
                apiCurrentPage={apiCurrentPage}
                key={ID}
                hasValue={!!Value}
                value={Value}
              >
                <FormField
                  ScreenOption={ScreenOption}
                  animateIndex={optionIndex}
                  tooltip={InformationLink}
                  lastOptionIndex={ScreenOptions.length - 1}
                />
              </AnimatedField>
            );
        });
    });

    return options;
  }, [apiCurrentPage, optionToFocus]);

  useEffect(() => {
    let vals = {};
    let validation = {};
    // Config option types
    const obj = {
      numericcontrol: "number",
      numerictextbox: "number",
      dropdown: "string",
      radiobuttonhorizontal: "string",
      imageandtexthorizontal: "string",
      typeabledropdown: "string",
      imagedropdown: "string",
      textbox: "string",
      checkbox: "boolean",
    };
    if (!api?.d?.Pages?.[currentPage]) return;

    const { Screens = [] } = api?.d?.Pages?.[currentPage] || {};
    Screens.forEach((Screen) => {
      const { ScreenOptions = [] } = Screen || {};
      ScreenOptions.forEach((ScreenOption) => {
        const {
          IsRequired,
          Name: name,
          Value,
          Ranges,
          DisplayType,
        } = ScreenOption || {};
        let Name = name.replace('["', "_").replace('"]', "");

        const { DisplayFormat } = ScreenOption || {};
        const { Max, Min } = Ranges?.[0] || {};
        const type = DisplayType.toLowerCase();
        let validationObj = yup?.[obj?.[type]]?.();
        if (!DisplayFormat || DisplayFormat !== "Fraction") {
          if (Min)
            validationObj = validationObj.min(
              Min,
              `This field can be no smaller than ${Min}`
            );
          if (Max)
            validationObj = validationObj.max(
              Max,
              `This field can be no larger than ${Max}`
            );

          if (type === "numerictextbox") {
            if (Min >= 0)
              validationObj = validationObj.positive(
                "Must be a positive number."
              );
          }
        }
        if (IsRequired && validationObj?.required)
          validationObj = validationObj.required("This field is required.");
        // if (Increment) validationObj = validationObj.step(Increment);
        if (type === "checkbox") {
          vals[Name] = Value === "True";
        } else {
          vals[Name] = Value;
        }
        validation[Name] = validationObj;
      });
    });

    setInitialValues(vals);
    setSchema(yup.object().shape(validation));
  }, [api, currentPage]);

  return isLoading ? (
    <Loading />
  ) : (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      validateOnBlur={false}
      validateOnMount={false}
      validateOnChange={false}
      onSubmit={null}
    >
      <Form style={{ paddingBottom: "10rem" }}>
        <Flex flexWrap="wrap">{screens}</Flex>
      </Form>
    </Formik>
  );
};

export default FormComponent;
